import { createSelector } from '@reduxjs/toolkit';

import { FeaturePatternTrussContainer } from '@paragon/contract-library';
import { TrussSolidCalculationDependencies } from '@paragon/layout';

import { RoofContainerBasedGeometricEcosystem } from '../../library/layout-geometry/ecosystem/RoofContainerBasedGeometricEcosystem';
import { RoofPlaneBasedGeometricEcosystem } from '../../library/layout-geometry/ecosystem/RoofPlaneBasedGeometricEcosystem';
import { getTrussSolidCalculationDependencies } from '../../library/layout-geometry/truss-envelopes/trussSolidSelectors';
import { RootState } from '../index';
import { getSingleSelectedItemEntryOrNull } from './getSelectedItemSelector';
import { getAllBearingEnvelopesDictionary, getVisibleLayoutObjectsCategorized } from './layoutDataSelectors';
import { isLayoutBasedOnRoofContainers } from './layoutModeSelectors';
import {
  getCalculatedRoofAndCeilingPlanes,
  getRoofOutlinesFromBearingsFeaturePatternContainersAndPlanes,
} from './roofAndCeilingPlaneSelectors';
import { getRoofContainerGuidDuringEnvelopeInsertion } from './roofContainerSelectors';
import { getActiveSnappingGuideLines } from './snappingGuideLineSelectors';

const getInsertingObjectInfo = (state: RootState) => state.application.insertingObjectInfo;

/** "Focused" here means either selected or undergoing insertion. */
export type FocusedFeaturePatternContainerData = FeaturePatternTrussContainer['shape'] | null;
const getFocusedFeaturePatternContainerData = createSelector(
  getSingleSelectedItemEntryOrNull,
  getInsertingObjectInfo,
  (selectedItem, insertingObjectInfo): FocusedFeaturePatternContainerData => {
    if (selectedItem?.type === 'featurePatternContainer') return selectedItem.item.shape;
    if (insertingObjectInfo?.type === 'featurePatternContainer') return insertingObjectInfo.data.shape;
    return null;
  },
);

export const getGeometricEcosystemForActivePortionOfLayout = createSelector(
  getVisibleLayoutObjectsCategorized,
  getAllBearingEnvelopesDictionary,
  isLayoutBasedOnRoofContainers,
  getRoofOutlinesFromBearingsFeaturePatternContainersAndPlanes,
  getFocusedFeaturePatternContainerData,
  getTrussSolidCalculationDependencies,
  getActiveSnappingGuideLines,
  getCalculatedRoofAndCeilingPlanes,

  (
    data,
    allBearingEnvelopes,
    inRoofContainerProject,
    roofOutlines,
    focusedFeaturePatternContainerData,
    dependencies,
    snappingGuideLines,
    allCalculatedPlanes,
  ) =>
    inRoofContainerProject
      ? new RoofContainerBasedGeometricEcosystem(
          data,
          allBearingEnvelopes,
          focusedFeaturePatternContainerData,
          dependencies,
          snappingGuideLines,
        )
      : new RoofPlaneBasedGeometricEcosystem(
          data,
          allBearingEnvelopes,
          allCalculatedPlanes,
          roofOutlines['visible'],
          focusedFeaturePatternContainerData,
          dependencies,
          snappingGuideLines,
        ),
);

export const getGeometricEcosystemForTrussEnvelopeInsertion = createSelector(
  isLayoutBasedOnRoofContainers,
  getRoofContainerGuidDuringEnvelopeInsertion,
  getVisibleLayoutObjectsCategorized,
  getAllBearingEnvelopesDictionary,
  getRoofOutlinesFromBearingsFeaturePatternContainersAndPlanes,
  getFocusedFeaturePatternContainerData,
  getTrussSolidCalculationDependencies,
  getActiveSnappingGuideLines,
  getCalculatedRoofAndCeilingPlanes,

  (
    inRoofContainerProject,
    roofContainerGuid,
    data,
    allBearingEnvelopes,
    roofOutlines,
    focusedFeaturePatternContainerData,
    dependencies,
    snappingGuideLines,
    allCalculatedPlanes,
  ) =>
    inRoofContainerProject
      ? roofContainerGuid
        ? RoofContainerBasedGeometricEcosystem.createForSingleRoofContainer(
            roofContainerGuid,
            data,
            allBearingEnvelopes,
            focusedFeaturePatternContainerData,
            dependencies,
            snappingGuideLines,
          )
        : new RoofContainerBasedGeometricEcosystem(
            data,
            allBearingEnvelopes,
            focusedFeaturePatternContainerData,
            dependencies,
            snappingGuideLines,
          )
      : new RoofPlaneBasedGeometricEcosystem(
          data,
          allBearingEnvelopes,
          allCalculatedPlanes,
          roofOutlines['visible'],
          focusedFeaturePatternContainerData,
          dependencies,
          snappingGuideLines,
        ),
);

export function getGeometricEcosystemScopedToSingleRoofContainer(
  state: RootState,
  activeRoofContainerGuid: string,
  activeTrussEnvelopeGuid: string | undefined,
  dependencies: TrussSolidCalculationDependencies,
) {
  return RoofContainerBasedGeometricEcosystem.createForSingleRoofContainer(
    activeRoofContainerGuid,
    getVisibleLayoutObjectsCategorized(state),
    getAllBearingEnvelopesDictionary(state),
    getFocusedFeaturePatternContainerData(state),
    dependencies,
    getActiveSnappingGuideLines(state),
    { excludedTrussEnvelopes: activeTrussEnvelopeGuid ? [activeTrussEnvelopeGuid] : undefined },
  );
}
